import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ShoppingCart, X } from "lucide-react";

const ACCENTS = {
  sage: "bg-sagelight text-pine",
  terra: "bg-blush text-terra",
  pine: "bg-pine text-cream",
  blushdeep: "bg-blush text-terra",
};

export default function ProductModal({ product, onClose, onBuy }) {
  useEffect(() => {
    if (product) window.__lenis?.stop?.();
    else window.__lenis?.start?.();
  }, [product]);

  return (
    <AnimatePresence>
      {product && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          aria-label={`Preview ${product.name}`}
        >
          <div
            data-testid="product-modal-backdrop"
            className="absolute inset-0 bg-pinedeep/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            data-testid="product-modal"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-xl overflow-hidden rounded-t-[2rem] bg-cream shadow-2xl sm:rounded-[2rem]"
          >
            <div
              className={`flex items-center justify-between px-7 py-5 ${
                ACCENTS[product.accent] || ACCENTS.sage
              }`}
            >
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] opacity-80">
                  {product.kind}
                </p>
                <h3 className="mt-1 font-serif text-3xl">{product.name}</h3>
              </div>
              <button
                data-testid="product-modal-close"
                onClick={onClose}
                aria-label="Close preview"
                className="rounded-full border border-current/20 p-2"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-7 py-6">
              <p className="text-sm leading-relaxed text-bodytext">{product.description}</p>
              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                What's inside
              </p>
              <ul className="mt-3 space-y-2.5">
                {product.inside.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-ink">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex items-center justify-between gap-4">
                <p className="font-serif text-3xl text-ink">
                  ${product.price.toFixed(0)}
                  <span className="ml-1 font-sans text-xs text-bodytext/60">USD · instant download</span>
                </p>
                <button
                  data-testid="product-modal-buy-button"
                  onClick={() => onBuy(product)}
                  className="flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:bg-pinedeep"
                >
                  <ShoppingCart className="h-4 w-4" /> Get it now
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
